// src/hooks/useAuth.js
import { createContext, useContext, useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from 'firebase/auth';
import {
  doc, getDoc, setDoc, updateDoc, deleteDoc, addDoc, collection,
  query, where, limit, getDocs, serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import {
  generateInviteCode, normaliseInviteCode, inviteProblem, inviteExpiryDate,
} from '../lib/invites';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [shop,    setShop]    = useState(null);  // tenant data
  const [profile, setProfile] = useState(null);  // user profile with role
  const [loading, setLoading] = useState(true);

  // 'loading' | 'ready' | 'missing' | 'error'
  //
  // Registration is not one atomic step: createUserWithEmailAndPassword signs
  // the user in immediately, which fires onAuthStateChanged BEFORE the profile
  // document has been written. Treating "no profile yet" as "needs to set up a
  // shop" made the app ask a brand-new owner for their shop name a second time.
  // Distinguishing these states is what stops that.
  const [profileState, setProfileState] = useState('loading');

  async function loadProfile(uid) {
    const snap = await getDoc(doc(db, 'users', uid));
    if (snap.exists()) {
      const prof = snap.data();
      setProfile(prof);
      const shopSnap = await getDoc(doc(db, 'tenants', prof.shopId));
      if (shopSnap.exists()) setShop({ id: shopSnap.id, ...shopSnap.data() });
      return 'ready';
    }

    // No profile. Before concluding they need to set a shop up, check whether
    // they already own one — that happens if registration was interrupted
    // between writing the tenant and writing the profile. Repair it silently
    // rather than making them re-enter details and risk a duplicate shop.
    const owned = await getDocs(
      query(collection(db, 'tenants'), where('ownerUid', '==', uid), limit(1))
    );
    if (!owned.empty) {
      const tenant = owned.docs[0];
      const repaired = {
        displayName: auth.currentUser?.displayName || '',
        email:       auth.currentUser?.email || '',
        shopId:      tenant.id,
        role:        'owner',
        createdAt:   serverTimestamp(),
      };
      await setDoc(doc(db, 'users', uid), repaired, { merge: true });
      setProfile(repaired);
      setShop({ id: tenant.id, ...tenant.data() });
      console.info('[auth] recovered an orphaned shop for this account');
      return 'ready';
    }

    return 'missing';
  }

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null); setShop(null); setProfile(null);
        setProfileState('loading'); setLoading(false);
        return;
      }
      setUser(firebaseUser);
      try {
        let state = await loadProfile(firebaseUser.uid);

        // registerShop writes the profile moments after sign-in. Give that
        // write a chance to land before showing anyone a setup form.
        if (state === 'missing') {
          await new Promise(r => setTimeout(r, 1200));
          state = await loadProfile(firebaseUser.uid);
        }
        setProfileState(state);
      } catch (err) {
        // A network or permissions failure is NOT the same as having no shop.
        // Showing the setup form here would invite a duplicate shop.
        console.error('Failed to load user profile/shop:', err);
        setProfileState('error');
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  /** Re-attempt the profile load after an error. */
  async function retryProfile() {
    if (!auth.currentUser) return;
    setProfileState('loading');
    try { setProfileState(await loadProfile(auth.currentUser.uid)); }
    catch (err) { console.error(err); setProfileState('error'); }
  }

  // Register a new shop + owner account
  async function registerShop({ email, password, shopName, ownerName, phone }) {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    const uid  = cred.user.uid;

    // Create a unique shopId from shop name
    const shopId = shopName.toLowerCase().replace(/[^a-z0-9]/g, '-') + '-' + uid.slice(0, 6);

    // Create tenant document
    await setDoc(doc(db, 'tenants', shopId), {
      name:           shopName,
      phone,
      ownerUid:       uid,
      plan:           'standard',
      deliveryFee:    12.00,
      serviceFee:     14.99,
      taxRate:        0.05,
      createdAt:      serverTimestamp(),
      lastOrderNumber: 1000,
    });

    // Create user profile
    const prof = {
      displayName: ownerName,
      email,
      shopId,
      role:        'owner',
      createdAt:   serverTimestamp(),
    };
    await setDoc(doc(db, 'users', uid), prof);

    // Add owner to employees sub-collection too
    await setDoc(doc(db, 'tenants', shopId, 'employees', uid), {
      displayName: ownerName,
      email,
      role:        'owner',
      status:      'active',
      uid,
      createdAt:   serverTimestamp(),
    });

    // Publish the profile straight away. The auth listener already fired when
    // the account was created — a moment before any of this existed — so
    // without this the app would decide the shop still needs setting up.
    setProfile(prof);
    const shopSnap = await getDoc(doc(db, 'tenants', shopId));
    if (shopSnap.exists()) setShop({ id: shopSnap.id, ...shopSnap.data() });
    setProfileState('ready');
  }

  // Finish setting up a shop for a user who is already signed in but has
  // no profile/tenant docs yet (e.g. registration was interrupted before
  // Firestore rules were deployed).
  async function completeShopSetup({ shopName, ownerName, phone }) {
    if (!user) throw new Error('Not signed in');
    const uid = user.uid;
    const shopId = shopName.toLowerCase().replace(/[^a-z0-9]/g, '-') + '-' + uid.slice(0, 6);

    await setDoc(doc(db, 'tenants', shopId), {
      name:           shopName,
      phone,
      ownerUid:       uid,
      plan:           'standard',
      deliveryFee:    12.00,
      serviceFee:     14.99,
      taxRate:        0.05,
      createdAt:      serverTimestamp(),
      lastOrderNumber: 1000,
    });

    await setDoc(doc(db, 'users', uid), {
      displayName: ownerName,
      email:       user.email,
      shopId,
      role:        'owner',
      createdAt:   serverTimestamp(),
    });

    await setDoc(doc(db, 'tenants', shopId, 'employees', uid), {
      displayName: ownerName,
      email:       user.email,
      role:        'owner',
      status:      'active',
      uid,
      createdAt:   serverTimestamp(),
    });

    const profileSnap = await getDoc(doc(db, 'users', uid));
    const prof = profileSnap.data();
    setProfile(prof);
    const shopSnap = await getDoc(doc(db, 'tenants', shopId));
    setShop({ id: shopSnap.id, ...shopSnap.data() });
    setProfileState('ready');
  }

  async function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  async function logout() {
    await signOut(auth);
  }

  async function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  /**
   * Invite a staff member.
   *
   * Creates an `invites/{code}` document alongside the employee record. The
   * code is the shareable secret — hand it over in person, by text, or via the
   * generated link. Returns it so the caller can display and copy it.
   */
  async function inviteEmployee({ email, displayName, role }) {
    if (!profile?.shopId) throw new Error('No shop found');
    const shopId = profile.shopId;
    const code   = normaliseInviteCode(generateInviteCode());

    const empRef = await addDoc(collection(db, 'tenants', shopId, 'employees'), {
      email, displayName, role: role || 'staff',
      status: 'invited', inviteCode: code, createdAt: serverTimestamp(),
    });

    // Readable by anyone holding the code (rules allow `get`, never `list`),
    // so it must not carry anything the new hire shouldn't see.
    await setDoc(doc(db, 'invites', code), {
      code,
      shopId,
      shopName:   shop?.name || '',
      email:      email || '',
      displayName: displayName || '',
      role:       role || 'staff',
      employeeId: empRef.id,
      revoked:    false,
      usedAt:     null,
      expiresAt:  Timestamp.fromDate(inviteExpiryDate()),
      createdAt:  serverTimestamp(),
      createdBy:  user?.uid || null,
    });

    return { code, employeeId: empRef.id };
  }

  /** Cancel an outstanding invite so the code stops working. */
  async function revokeInvite(code, employeeId) {
    const c = normaliseInviteCode(code);
    if (c) await updateDoc(doc(db, 'invites', c), { revoked: true });
    if (employeeId && profile?.shopId) {
      await deleteDoc(doc(db, 'tenants', profile.shopId, 'employees', employeeId));
    }
  }

  /**
   * Redeem an invite: create the Firebase Auth user, then point their profile
   * at the EXISTING shop rather than spinning up a new tenant.
   *
   * The invite is validated client-side for a friendly message, and again by
   * Firestore rules on write — the rules are what actually enforce it, since
   * anything here could be bypassed.
   */
  async function joinShopWithInvite({ code, email, password, displayName }) {
    const c = normaliseInviteCode(code);
    const snap = await getDoc(doc(db, 'invites', c));
    const invite = snap.exists() ? snap.data() : null;

    const problem = inviteProblem(invite, { email });
    if (problem) throw new Error(problem);

    const cred = await createUserWithEmailAndPassword(auth, email, password);
    const uid  = cred.user.uid;

    const prof = {
      displayName: displayName || invite.displayName || '',
      email,
      shopId:      invite.shopId,
      role:        invite.role,
      inviteCode:  c,          // rules cross-check this against the invite doc
      createdAt:   serverTimestamp(),
    };
    await setDoc(doc(db, 'users', uid), prof);

    // Promote the placeholder employee record to a real, linked account.
    if (invite.employeeId) {
      await setDoc(
        doc(db, 'tenants', invite.shopId, 'employees', invite.employeeId),
        { displayName: prof.displayName, email, role: invite.role,
          status: 'active', uid, joinedAt: serverTimestamp() },
        { merge: true }
      );
    }

    await updateDoc(doc(db, 'invites', c), { usedAt: serverTimestamp(), usedByUid: uid });

    setProfile(prof);
    const shopSnap = await getDoc(doc(db, 'tenants', invite.shopId));
    if (shopSnap.exists()) setShop({ id: shopSnap.id, ...shopSnap.data() });
    setProfileState('ready');
  }


  return (
    <AuthContext.Provider value={{
      user, shop, profile, loading, profileState, retryProfile,
      registerShop, completeShopSetup, login, logout, resetPassword,
      inviteEmployee, revokeInvite, joinShopWithInvite,
      shopId: profile?.shopId,
      isOwner: profile?.role === 'owner',
      isManager: ['owner','manager'].includes(profile?.role),
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
